/**
 * Socios.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "¿Cuánto sacó cada uno, y quién sacó más?"
 *
 * El neto (retiros en plata y en mercaderia, menos aportes) va en dolares,
 * porque en pesos un retiro de marzo y uno de septiembre no se comparan.
 *
 * SE CARGA DESDE ACA, PERO NO ES OTRA PUERTA
 * ------------------------------------------
 * Cada opcion de «Cargar» abre los MISMOS dialogos que Caja e Inventario, con la
 * categoria ya elegida. La plata sigue saliendo de una caja real y la
 * mercaderia sigue descontando stock: el arqueo y el inventario no se enteran
 * de que el movimiento se cargo desde aca.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  ChevronDown,
  HandCoins,
  PackageMinus,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { formatear } from "@tora/shared";

import { DialogMovimiento } from "@/components/caja/DialogMovimiento";
import { DialogSalida } from "@/components/catalogo/DialogsMovimiento";
import { PageHeader } from "@/components/layout/PageHeader";
import { DialogSueldos } from "@/components/socios/DialogSueldos";
import { Pagina } from "@/components/layout/Pagina";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SelectorPeriodo } from "@/components/ui/selector-periodo";
import { DetalleSocios, SinAsignar } from "@/components/socios/Detalle";
import { TarjetasSocios } from "@/components/socios/Tarjetas";
import { resumirSocios } from "@/components/socios/logica";
import { mostrarError, mostrarExito } from "@/lib/errores";
import {
  useAsignarSocio,
  useCajas,
  useCuentasFinancieras,
  useMovimientosSocios,
  useProductos,
  useRegistrarMovimiento,
  useSalidaStock,
  useFijarSueldoSocio,
  useSocios,
} from "@/lib/queries";
import { esteMes, rangoDelPeriodo, type Periodo } from "@/lib/periodo";
import { TOPE_FILAS } from "@/lib/usar-ver-mas";

export const Route = createFileRoute("/socios")({ component: Socios });

/** Lo que abre cada boton. Las de caja son claves de `OPERACIONES`. */
type Carga = "cerrado" | "RETIRO" | "APORTE" | "SUELDO_SOCIO" | "MERCADERIA";

/**
 * Una sola primaria, «Cargar», y lo que se carga en su menu (MASTER §13.2).
 * Eran cuatro botones iguales en fila: competian entre si y empujaban el
 * resumen para abajo. El orden es el de la frecuencia.
 */
const OPCIONES_CARGA: ReadonlyArray<{
  carga: Exclude<Carga, "cerrado">;
  etiqueta: string;
  icono: LucideIcon;
}> = [
  { carga: "RETIRO", etiqueta: "Retiro en plata", icono: ArrowUpRight },
  { carga: "MERCADERIA", etiqueta: "Retiro de mercadería", icono: PackageMinus },
  { carga: "SUELDO_SOCIO", etiqueta: "Sueldo", icono: Banknote },
  { carga: "APORTE", etiqueta: "Aporte", icono: ArrowDownLeft },
];

const ETIQUETA_CARGA: Partial<Record<string, string>> = {
  RETIRO: "Retiro",
  APORTE: "Aporte",
  SUELDO: "Sueldo",
};

/**
 * El mismo tamaño que Inventario, que comparten la cache de productos, y el
 * tope del backend. Con 300 el selector de «Retiro de mercadería» no
 * encontraba lo que viene despues en el orden alfabetico (los VIDRIO TEMPLADO,
 * las WALLET): hay 328 activos y la pagina cortaba en «VAPE KING».
 */
const PRODUCTOS_POR_PAGINA = TOPE_FILAS;

function Socios(): JSX.Element {
  const [periodo, setPeriodo] = useState<Periodo>(() => esteMes());
  const rango = useMemo(() => rangoDelPeriodo(periodo), [periodo]);
  const [filtroSocio, setFiltroSocio] = useState("todos");
  const [carga, setCarga] = useState<Carga>("cerrado");
  const [abrirSueldos, setAbrirSueldos] = useState(false);
  const fijarSueldo = useFijarSueldoSocio();

  const { data: socios } = useSocios();
  const { data: movimientos } = useMovimientosSocios(rango);
  const asignar = useAsignarSocio();

  // Los usan los dialogos de carga. Se traen al entrar para que abran llenos;
  // son las mismas consultas que Caja e Inventario, asi que casi siempre ya
  // estan en la cache.
  const { data: cuentas } = useCuentasFinancieras();
  // Los saldos, para que el retiro avise si deja una caja en negativo.
  const { data: cajas } = useCajas();
  const { data: productos } = useProductos({ limite: PRODUCTOS_POR_PAGINA });
  const registrar = useRegistrarMovimiento();
  const salir = useSalidaStock();

  const listaSocios = useMemo(() => socios?.filas ?? [], [socios]);
  const listaCuentas = useMemo(() => cuentas?.filas ?? [], [cuentas]);
  const listaProductos = useMemo(() => productos?.filas ?? [], [productos]);
  const filas = useMemo(() => movimientos?.filas ?? [], [movimientos]);
  const resumen = useMemo(() => resumirSocios(listaSocios, filas), [listaSocios, filas]);

  // Si se esta mirando a un socio, el dialogo se abre con el. Mirando a los
  // dos, no se elige ninguno: el que viene puesto es el que queda.
  const socioInicial = filtroSocio === "todos" ? undefined : Number(filtroSocio);
  const cerrar = () => setCarga("cerrado");

  return (
    <Pagina alto="fijo">
      <PageHeader
        titulo="Socios"
        icono={HandCoins}
        acciones={
          <>
            <SelectorPeriodo valor={periodo} onChange={setPeriodo} />
            <Button type="button" variant="outline" onClick={() => setAbrirSueldos(true)}>
              Sueldos pactados
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button">
                  <Plus aria-hidden="true" />
                  Cargar
                  <ChevronDown aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {OPCIONES_CARGA.map((o) => (
                  <DropdownMenuItem key={o.carga} onSelect={() => setCarga(o.carga)}>
                    <o.icono className="size-4" aria-hidden="true" />
                    {o.etiqueta}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <SinAsignar
        socios={listaSocios}
        movimientos={movimientos?.sin_asignar ?? []}
        asignando={asignar.isPending}
        onAsignar={(movimientoId, socioId) =>
          asignar.mutate(
            { movimientoId, socioId },
            {
              onSuccess: () =>
                mostrarExito(
                  "Listo",
                  `Quedó a nombre de ${listaSocios.find((s) => s.id === socioId)?.nombre ?? "el socio"}`,
                ),
              onError: mostrarError,
            },
          )
        }
      />

      <TarjetasSocios resumen={resumen} />

      <DialogSueldos
        abierto={abrirSueldos}
        onCerrar={() => setAbrirSueldos(false)}
        socios={listaSocios}
        guardando={fijarSueldo.isPending}
        onGuardar={(sueldo) =>
          fijarSueldo.mutate(sueldo, {
            onSuccess: () =>
              mostrarExito(
                "Sueldo pactado guardado",
                listaSocios.find((x) => x.id === sueldo.id)?.nombre,
              ),
            onError: mostrarError,
          })
        }
      />

      <DetalleSocios
        socios={listaSocios}
        movimientos={filas}
        socio={filtroSocio}
        onSocioChange={setFiltroSocio}
      />

      <DialogMovimiento
        abierto={carga !== "cerrado" && carga !== "MERCADERIA"}
        onAbiertoChange={(a) => !a && cerrar()}
        cuentas={listaCuentas}
        ambito="socios"
        socios={listaSocios}
        saldos={cajas ?? []}
        operacionInicial={carga === "cerrado" || carga === "MERCADERIA" ? "RETIRO" : carga}
        socioInicial={socioInicial}
        guardando={registrar.isPending}
        onConfirmar={(datos) =>
          registrar.mutate(datos, {
            onSuccess: () => {
              cerrar();
              const cuenta = listaCuentas.find((c) => c.id === datos.cuenta_id);
              const socio = listaSocios.find((s) => s.id === datos.socio_id);
              mostrarExito(
                `${ETIQUETA_CARGA[datos.concepto ?? "RETIRO"] ?? "Movimiento"} de ${socio?.nombre ?? "socio"}: ${formatear(datos.importe, cuenta?.moneda ?? "ARS")}`,
                cuenta ? `${cuenta.nombre} · ${cuenta.moneda}` : undefined,
              );
            },
            onError: mostrarError,
          })
        }
      />

      <DialogSalida
        abierto={carga === "MERCADERIA"}
        onAbiertoChange={(a) => !a && cerrar()}
        productos={listaProductos}
        socios={listaSocios}
        tipoInicial="RETIRO_SOCIO"
        motivoFijo
        socioInicial={socioInicial}
        guardando={salir.isPending}
        onConfirmar={(datos) =>
          salir.mutate(datos, {
            onSuccess: () => {
              cerrar();
              const nombre = listaProductos.find((p) => p.id === datos.producto_id)?.nombre;
              const socio = listaSocios.find((s) => s.id === datos.socio_id);
              mostrarExito(
                `${socio?.nombre ?? "El socio"} se llevó ${datos.cantidad} u.${nombre ? ` de ${nombre}` : ""}`,
              );
            },
            onError: mostrarError,
          })
        }
      />
    </Pagina>
  );
}
